import { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarClock, Pencil, Plus, Trash2 } from "lucide-react";
import { Avatar, CheckBox, ConfirmDialog } from "../crm/ui";
import { ChoiceRow, Drawer, DrawerField, DrawerGroup, drawerInput } from "../Drawer";
import { PrimaryButton } from "../ui";
import {
  DAY, GhostButton, SectionCard, TASK_STATUSES, TASK_STATUS_KEY, TaskStatusBadge, TimeInput,
  apiCall, apiGet, currentUser, dateValue, fmtDateTime, fmtShort, initialsFor, localDay, timeValue, usePeople,
} from "../crm/contactsKit";
import { PRIORITY_LABEL, SEVERITIES, SeverityWord, severityKey } from "./supportKit";
import { NO_DATA } from "../../lib/format";
import { PageLoader } from "../PageLoader";

/**
 * Complaint → Scheduled tasks, on the complaint tasks API (as the old complaint page):
 *   list ........... GET    /api/complaints/:id/tasks
 *   add ............ POST   /api/complaints/:id/tasks { title, description, due_date, due_time, priority, assigned_to }
 *   edit, status ... PUT    /api/complaints/:id/tasks/:taskId { …same, status }
 *   mark done ...... PUT    /api/complaints/:id/tasks/:taskId { status: "completed" }
 *   delete ......... DELETE /api/complaints/:id/tasks/:taskId
 * People to assign: GET /api/staff/assignable-users. The backend logs "Task
 * scheduled" / "Task completed" on the complaint timeline itself, so `onChanged`
 * lets the page reload its activities.
 * Not wired: a task type (the old form had one, but the backend doesn't save it).
 */

const STATUS_LABEL = Object.fromEntries(Object.entries(TASK_STATUS_KEY).map(([label, key]) => [key, label]));
const firstName = (name) => name.split(/\s+/)[0] ?? name;

/** complaint_tasks row → what the card and drawer show. */
function toComplaintTask(row) {
  const day = localDay(row.due_date);
  const [h, m] = String(row.due_time || "").split(":").map(Number);
  const hasTime = !!row.due_time && Number.isFinite(h);
  const due = day === null ? null : hasTime ? day + (h * 60 + (m || 0)) * 60_000 : day + DAY - 60_000;
  const status = STATUS_LABEL[row.status] || "To do"; // "overdue" shows as To do; the card flags lateness from the due time
  return {
    id: row.id,
    title: row.title || "",
    notes: row.description || "",
    due,
    hasTime,
    priority: PRIORITY_LABEL[row.priority] || "Medium",
    status,
    done: row.status === "completed",
    rawStatus: row.status,
    assigneeId: row.assigned_to || "",
    assignee: row.assigned_to_name || "",
    createdBy: row.created_by_name || "",
  };
}

function dueText(task) {
  if (!task.due) return "No due date";
  return task.hasTime ? fmtDateTime(task.due) : fmtShort(task.due);
}

function ComplaintTaskDrawer({ open, onClose, complaintId, task, onSaved, onDelete }) {
  const people = usePeople();
  const me = currentUser().id;
  const blank = useCallback(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0);
    return { title: "", dueDate: dateValue(d.getTime()), dueTime: timeValue(d.getTime()), priority: "Medium", assignee: me ? String(me) : "", notes: "", status: "To do" };
  }, [me]);
  const [form, setForm] = useState(blank);
  const [initial, setInitial] = useState(blank);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = task
      ? { title: task.title, dueDate: task.due ? dateValue(task.due) : "", dueTime: task.hasTime ? timeValue(task.due) : "", priority: task.priority, assignee: task.assigneeId ? String(task.assigneeId) : "", notes: task.notes, status: task.status }
      : blank();
    setForm(next);
    setInitial(next);
    setErrors({});
  }, [open, task, blank]);

  const assignees = useMemo(() => {
    const list = people.map((p) => ({ id: String(p.id), name: p.name }));
    if (task?.assigneeId && !list.some((p) => p.id === String(task.assigneeId))) list.push({ id: String(task.assigneeId), name: task.assignee || "Previous assignee" });
    return list;
  }, [people, task]);
  const dirty = Object.keys(form).some((k) => form[k] !== initial[k]);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const quickDue = (kind) => {
    const d = new Date();
    if (kind === "hour") d.setHours(d.getHours() + 1, 0, 0, 0);
    if (kind === "today") d.setHours(18, 0, 0, 0);
    if (kind === "tomorrow") { d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); }
    if (kind === "week") { d.setDate(d.getDate() + 7); d.setHours(10, 0, 0, 0); }
    set({ dueDate: dateValue(d.getTime()), dueTime: timeValue(d.getTime()) });
    setErrors((e) => ({ ...e, due: undefined }));
  };

  async function save() {
    if (saving) return;
    const title = form.title.trim();
    const next = { ...(title ? {} : { title: "Add a title so the team knows what to do." }), ...(form.dueDate ? {} : { due: "Choose a due date." }) };
    setErrors(next);
    if (Object.keys(next).length) return;
    const body = {
      title,
      description: form.notes.trim(),
      due_date: form.dueDate,
      due_time: form.dueTime ? `${form.dueTime}:00` : null,
      priority: severityKey(form.priority),
      assigned_to: form.assignee ? Number(form.assignee) : null,
    };
    setSaving(true);
    try {
      if (task) await apiCall("put", `/api/complaints/${complaintId}/tasks/${task.id}`, { ...body, status: form.status === initial.status ? task.rawStatus : TASK_STATUS_KEY[form.status] });
      else await apiCall("post", `/api/complaints/${complaintId}/tasks`, body);
      setSaving(false);
      onSaved(task ? "Task updated." : "Task scheduled.");
      onClose();
    } catch (e) {
      setSaving(false);
      setErrors({ save: e.message });
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={task ? "Edit task" : "Schedule task"}
      context="Complaint follow-up"
      width="lg"
      dirty={dirty}
      danger={task ? <button type="button" onClick={() => onDelete(task)} className="flex h-10 items-center gap-2 rounded-full px-3 text-small font-semibold text-danger hover:bg-danger-soft"><Trash2 size={15} strokeWidth={1.75} />Delete task</button> : undefined}
      actions={<PrimaryButton type="button" onClick={save} loading={saving} loadingLabel="Saving…" className="h-11 px-5 text-small">{task ? "Save changes" : "Schedule task"}</PrimaryButton>}
    >
      <div className="flex flex-col gap-6">
        {task && <p className="-mt-2 flex flex-wrap items-center gap-2 text-caption text-muted">Created by {task.createdBy || NO_DATA}<span aria-hidden>·</span><TaskStatusBadge status={form.status} /></p>}
        <DrawerGroup>
          <input aria-label="Task title" data-autofocus value={form.title} onChange={(e) => { set({ title: e.target.value }); setErrors((x) => ({ ...x, title: undefined })); }} placeholder="What needs to happen?" className="w-full border-0 border-b border-line bg-transparent px-0 pb-3 text-h3 font-semibold text-ink outline-none placeholder:text-muted/60 focus:border-ink" />
          {errors.title && <p role="alert" className="text-caption text-danger">{errors.title}</p>}
        </DrawerGroup>
        <DrawerGroup title="When">
          <div className="flex flex-wrap gap-2">
            {[["hour", "In 1 hour"], ["today", "Today 6 PM"], ["tomorrow", "Tomorrow 10 AM"], ["week", "In a week"]].map(([k, l]) => <button key={k} type="button" onClick={() => quickDue(k)} className="rounded-full bg-mist px-3 py-1.5 text-caption font-medium hover:bg-line">{l}</button>)}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <DrawerField label="Date" htmlFor="ctask-due-date"><input id="ctask-due-date" type="date" className={drawerInput} value={form.dueDate} onChange={(e) => { set({ dueDate: e.target.value }); setErrors((x) => ({ ...x, due: undefined })); }} /></DrawerField>
            <DrawerField label="Time" htmlFor="ctask-due-time" optional><TimeInput id="ctask-due-time" size="sm" value={form.dueTime} onChange={(v) => set({ dueTime: v })} /></DrawerField>
          </div>
          {errors.due && <p role="alert" className="text-caption text-danger">{errors.due}</p>}
        </DrawerGroup>
        <DrawerGroup>
          <DrawerField label="Priority"><ChoiceRow value={form.priority} options={SEVERITIES} onChange={(v) => set({ priority: v })} label="Priority" /></DrawerField>
          <DrawerField label="Assigned to">
            <div role="radiogroup" aria-label="Assigned to" className="flex flex-wrap gap-2">
              <button type="button" role="radio" aria-checked={!form.assignee} onClick={() => set({ assignee: "" })} className={`flex h-10 items-center rounded-full px-3.5 text-caption font-medium ring-1 ${!form.assignee ? "bg-mist text-ink ring-ink" : "bg-paper text-muted ring-line hover:text-ink"}`}>Unassigned</button>
              {assignees.map((p) => {
                const selected = form.assignee === p.id;
                return <button key={p.id} type="button" role="radio" aria-checked={selected} onClick={() => set({ assignee: p.id })} className={`flex h-10 items-center gap-2 rounded-full px-2.5 pr-3 text-caption font-medium ring-1 ${selected ? "bg-mist text-ink ring-ink" : "bg-paper text-muted ring-line hover:text-ink"}`}><Avatar name={p.name} initials={initialsFor(p.name)} size="sm" />{firstName(p.name)}</button>;
              })}
            </div>
          </DrawerField>
        </DrawerGroup>
        {task && <DrawerGroup title="Status"><ChoiceRow value={form.status} options={TASK_STATUSES} onChange={(v) => set({ status: v })} label="Task status" /></DrawerGroup>}
        <DrawerGroup title="Notes" hint="Add context that will help the team take the next step.">
          <DrawerField label="Notes" htmlFor="ctask-notes" optional><textarea id="ctask-notes" rows={4} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Add context for the team" className={`${drawerInput} h-auto py-3`} /></DrawerField>
        </DrawerGroup>
        {errors.save && <p role="alert" className="text-caption text-danger">{errors.save}</p>}
      </div>
    </Drawer>
  );
}

/** "Scheduled tasks" card for the complaint detail page. */
export default function ComplaintTasks({ complaintId, onChanged, onMessage }) {
  const [tasks, setTasks] = useState([]);
  const [loadedFor, setLoadedFor] = useState(null); // the complaint `tasks` belong to
  const loaded = loadedFor === complaintId;
  const [reload, setReload] = useState(0);
  const [drawer, setDrawer] = useState(null); // { task } | null
  const [deleting, setDeleting] = useState(null);

  useEffect(() => {
    let alive = true;
    apiGet(`/api/complaints/${complaintId}/tasks`).then((r) => { if (!alive) return; setTasks((r?.data || []).map(toComplaintTask)); setLoadedFor(complaintId); });
    return () => { alive = false; };
  }, [complaintId, reload]);

  const changed = (message) => { setReload((n) => n + 1); onChanged?.(); if (message) onMessage?.(message); };
  const markDone = (task) => apiCall("put", `/api/complaints/${complaintId}/tasks/${task.id}`, { status: "completed" }).then(() => changed("Task marked as done.")).catch((e) => onMessage?.(e.message));
  const remove = (task) => apiCall("delete", `/api/complaints/${complaintId}/tasks/${task.id}`).then(() => changed("Task deleted.")).catch((e) => onMessage?.(e.message));
  const open = tasks.filter((t) => !t.done).length;
  const now = Date.now();

  return (
    <SectionCard title="Scheduled tasks" action={<GhostButton onClick={() => setDrawer({ task: null })} className="h-9 px-3 text-small"><Plus size={15} />Add task</GhostButton>}>
      {!loaded ? <PageLoader label="Loading tasks…" className="py-8" /> : tasks.length ? (
        <ul className="flex flex-col divide-y divide-line">
          {tasks.map((t) => {
            const overdue = !t.done && t.status !== "Cancelled" && t.due && t.due < now;
            return (
              <li key={t.id} className="flex items-start gap-3 py-3 first:pt-0 last:pb-0">
                <span className="pt-0.5"><CheckBox checked={t.done} onChange={() => !t.done && markDone(t)} label={t.done ? `${t.title} is done` : `Mark ${t.title} as done`} /></span>
                <button type="button" onClick={() => setDrawer({ task: t })} className="min-w-0 flex-1 text-left">
                  <span className={`block truncate text-small font-semibold ${t.done || t.status === "Cancelled" ? "text-muted line-through" : ""}`}>{t.title}</span>
                  {t.notes && <span className="mt-0.5 block truncate text-caption text-muted">{t.notes}</span>}
                  <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-caption text-muted">
                    <span className={`flex items-center gap-1 ${overdue ? "font-semibold text-danger" : ""}`}><CalendarClock size={13} strokeWidth={1.75} />{overdue ? "Overdue · " : ""}{dueText(t)}</span>
                    <span aria-hidden>·</span><span>{t.assignee || "Unassigned"}</span>
                    {t.status !== "To do" && <TaskStatusBadge status={t.status} />}
                  </span>
                </button>
                <SeverityWord value={t.priority} />
                <span className="flex items-center gap-0.5">
                  <button type="button" onClick={() => setDrawer({ task: t })} aria-label={`Edit ${t.title}`} title="Edit" className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-ink"><Pencil size={15} strokeWidth={1.75} /></button>
                  <button type="button" onClick={() => setDeleting(t)} aria-label={`Delete ${t.title}`} title="Delete" className="flex h-8 w-8 items-center justify-center rounded-full text-muted hover:bg-mist hover:text-danger"><Trash2 size={15} strokeWidth={1.75} /></button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : <p className="text-small text-muted">No scheduled tasks. Add a follow-up call, callback or investigation step for the team.</p>}
      {loaded && tasks.length > 0 && <p className="-mt-2 text-caption text-muted">{open} open of {tasks.length}</p>}
      <ComplaintTaskDrawer open={!!drawer} onClose={() => setDrawer(null)} complaintId={complaintId} task={drawer?.task || null} onSaved={changed} onDelete={(t) => { setDrawer(null); setDeleting(t); }} />
      {deleting && <ConfirmDialog title="Delete this task?" body={<p>Remove “{deleting.title}” from this complaint? This can't be undone.</p>} confirmLabel="Delete task" onCancel={() => setDeleting(null)} onConfirm={() => { const t = deleting; setDeleting(null); remove(t); }} />}
    </SectionCard>
  );
}
